// ─────────────────────────────────────────────────────────────────────────────
// REQ-103 — Panel productivo para enviar un reconocimiento.
//
// FASE 4 (paridad demo, H-35..H-44): estructura y clases `.rp-single`/`.rp-*` calcadas del demo
// (`sigma-tc-demo/src/components/ReconocerPanel.tsx`), con el flujo real conectado a la API
// (CERO mocks) del integrado:
//   1. Búsqueda de personas contra el UserAdminService (usePeopleSearch, debounced).
//   2. Selección de Fundamental → comportamientos (solo los que traen actitudes, p.ej. F01)
//      → daily habits, obtenido del catálogo real (useRecognitionCatalog).
//   3. Límite de fundamentales según la config de la OU (Europa/GDPR = 1, resto = 2). El backend
//      es la fuente de verdad: valida y devuelve 422/403; el front refleja el mensaje.
//   4. Resumen editable, mensaje, envío con useCreateRecognition, animación de éxito y refresco
//      automático del muro y de la racha (invalidación de queries).
//   5. Carga masiva embebida al final del panel (H-42) — el botón suelto de ReconocerView
//      desaparece; el modal (BulkUploadModal) sigue siendo el destino de "Subir CSV".
//
// Regla de oro: los puntos NUNCA se muestran. La autorización real la manda el backend.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmojiPickerButton } from '@/components/common/EmojiPickerButton';
import { PersonPicker } from '@/components/common';
import type { PersonaElegida } from '@/components/common';
import { insertAtCursor } from '@/utils/insertAtCursor';
import { useAutoGrowTextarea } from '@/hooks/useAutoGrowTextarea';
import {
  useRecognitionCatalog,
  useCreateRecognition,
  useRecognitionRules,
  useNextBrillamos,
  getFundamentalName,
  getFundamentalColor,
  getBehaviorName,
  getHabitName,
  availableHabitsFor,
  getRecognitionErrorStatus,
  MENSAJE_MAX_LENGTH,
} from '@/api/recognition';
import type {
  PersonSummary,
  FundamentalDto,
  AttitudeBehaviorDto,
  DailyHabitDto,
  CreateRecognitionBody,
  RecognitionOrigin,
} from '@/api/recognition';
import { useUserContext } from '@/contexts/UserContext';
import { getInitials } from '@/utils/strings';
import { useRecognitionPermissions } from './access/useRecognitionPermissions';
import { StateBlock } from './components';
import { MessageLimitHint, recognitionSendErrorMessage } from './messageLimit';
import { getFundamentalIcon, getBehaviorIcon } from './fundamentalAssets';
import { placeholderDelMensaje, fundamentalesOfrecibles } from './postitShared';
import { useUnsavedChangesGuard } from '@/contexts/UnsavedChangesContext';
import { useVistaHabilitada } from './useVistas';

/** Iniciales para el avatar a partir del nombre o el correo. */
function initials(persona: PersonSummary): string {
  return getInitials(persona.displayName || persona.email);
}

/**
 * Adapta la selección del `PersonPicker` compartido (`PersonaElegida`, extraído de este mismo
 * archivo) al `PersonSummary` que maneja el resto del panel. El correo nunca llega nulo desde este
 * buscador real —viene de `PersonSummary.email`, que no es anulable—; se completa igual porque el
 * tipo compartido sí lo admite (lo usan buscadores donde un frontline puede no tener correo).
 */
function aPersonSummary(persona: PersonaElegida): PersonSummary {
  return {
    id: persona.id,
    displayName: persona.displayName,
    email: persona.email ?? '',
    photoUrl: persona.photoUrl,
    avatar: persona.avatar,
  };
}

interface DelegateSectionProps {
  mode: 'self' | 'onBehalf';
  onModeChange: (mode: 'self' | 'onBehalf') => void;
  onBehalfOf: PersonSummary | null;
  onPick: (p: PersonSummary) => void;
  onRemove: () => void;
  excludeId: string;
}

/**
 * Pestañas "A mi nombre" / "En representación de otro" (H-43 rediseño): separan el MODO de envío
 * de la lista de destinatarios de abajo. Antes era un banner + picker siempre visibles justo
 * encima del buscador de destinatarios, y los usuarios confundían un buscador con el otro.
 * Extraído a su propio componente para no sumarle ramas a la complejidad cognitiva de
 * `ReconocerPanel`, ya alta de por sí.
 */
function DelegateSection({ mode, onModeChange, onBehalfOf, onPick, onRemove, excludeId }: Readonly<DelegateSectionProps>) {
  const { t } = useTranslation('recognition');
  return (
    <div className="rp-section rp-delegate-section">
      <div className="rp-delegate-tabs" role="tablist" aria-label={t('panel.delegateTitle')}>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'self'}
          className={`rp-delegate-tab ${mode === 'self' ? 'rp-delegate-tab--active' : ''}`}
          onClick={() => onModeChange('self')}
        >
          <i className="fas fa-user" />
          {t('panel.modeSelfTab')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'onBehalf'}
          className={`rp-delegate-tab ${mode === 'onBehalf' ? 'rp-delegate-tab--active' : ''}`}
          onClick={() => onModeChange('onBehalf')}
        >
          <i className="fas fa-user-pen" />
          {t('panel.modeOnBehalfTab')}
        </button>
      </div>
      {mode === 'onBehalf' && (
        <div className="rp-supervisor-section">
          <p className="rp-supervisor-hint">{t('panel.delegateSubtitle')}</p>
          <label className="rp-section-label">{t('panel.delegateLabel')}</label>
          {onBehalfOf ? (
            <div className="rp-chips-row">
              <span className="rp-chip-person">
                <span className="rp-chip-av">{initials(onBehalfOf)}</span>
                {onBehalfOf.displayName ?? onBehalfOf.email}
                <button className="btn btn--secondary" type="button" onClick={onRemove} aria-label={t('common.remove')}>
                  <i className="fas fa-xmark" />
                </button>
              </span>
            </div>
          ) : (
            // `value` queda SIEMPRE en `null`: el chip de "a nombre de" ya lo pinta el bloque de
            // arriba (`onBehalfOf ? ... : <PersonPicker …>`), así que el picker jamás debe quedarse
            // con la selección puesta — solo avisa al panel (`onPick`) y el campo sigue libre.
            <PersonPicker
              value={null}
              placeholder={t('panel.delegatePlaceholder')}
              excludeIds={[excludeId]}
              onChange={(persona) => { if (persona) onPick(aPersonSummary(persona)); }}
              // Punto 12 (revisión final, 2026-09-03) — variante de borde naranja, recuperada de
              // `.rp-search-row--supervisor`: se perdió al migrar este buscador a `PersonPicker`
              // compartido. Antes de la migración viajaba SIEMPRE `true` para el picker de
              // delegación (ver `git show b6d9aed0` — el único llamador que la pasaba).
              isSupervisorVariant
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Delegado gana siempre. Si no, el origen depende de la PANTALLA desde la que se envía
 * (`isBrillamosScreen`), no de si hay una campaña activa: `/app/brillamos` pinta el panel de
 * reconocer con o sin campaña (`{(!campaign || active) && ...}` en `BrillamosView.tsx`), así que
 * atar el origen a `campaignId` dejaba como 'Diario' cualquier envío sin campaña activa — bug
 * detectado 2026-08-06 vía evidencia del payload real (`campanaId: null`, sin campaña vigente).
 */
function resolveOrigin(isDelegated: boolean, isBrillamosScreen: boolean): RecognitionOrigin {
  if (isDelegated) return 'Delegado';
  return isBrillamosScreen ? 'Brillamos' : 'Diario';
}

interface MissingHintsParams {
  recipients: PersonSummary[];
  fundamentalIds: string[];
  messageOk: boolean;
  minChars: number;
  dailyHabitRequired: boolean;
  habitIds: string[];
  availableHabits: DailyHabitDto[];
  behaviors: AttitudeBehaviorDto[];
  behaviorIds: string[];
  sendingDisabled: boolean;
  scheduleRestricted: boolean;
  isPending: boolean;
}

/**
 * Regla "sin errores silenciosos": el botón deshabilitado por sí solo no explica nada — hay que
 * decirle al usuario QUÉ le falta. `sendingDisabled`/`isPending` ya tienen su propio mensaje/estado
 * (kill-switch y "Enviando…"), y el caso "hay hábitos para elegir pero no elegiste ninguno" ya lo
 * cubre `habitRequiredHint` DENTRO de la sección de hábitos — así que esta función solo agrega lo
 * que hoy no se explica en ningún otro lado: campos vacíos, y las dos variantes de "no puedo
 * mostrarte hábitos todavía" (falta elegir comportamiento, o el Fundamental no tiene ninguno
 * configurado). Extraída del componente (en vez de inline) para no sumar más ramas a su
 * complejidad cognitiva, ya alta de por sí.
 */
function buildMissingHints(
  params: MissingHintsParams,
  t: (key: string, options?: Record<string, unknown>) => string,
): string[] {
  const {
    recipients, fundamentalIds, messageOk, minChars, dailyHabitRequired, habitIds,
    availableHabits, behaviors, behaviorIds, sendingDisabled, scheduleRestricted, isPending,
  } = params;
  // R7 — con el envío bloqueado por kill-switch, excepción de horario o pendiente, ningún otro
  // hint aporta: el problema no es lo que falta llenar, es que ahora mismo no se puede enviar.
  if (sendingDisabled || scheduleRestricted || isPending) return [];

  const hasFundamental = fundamentalIds.length > 0;
  const hints: string[] = [];
  if (recipients.length === 0) hints.push(t('panel.missingRecipientsHint'));
  if (!hasFundamental) hints.push(t('panel.missingFundamentalHint'));
  if (hasFundamental && !messageOk) hints.push(t('panel.messageMinCharsHint', { count: minChars }));
  if (hasFundamental && dailyHabitRequired && habitIds.length === 0 && availableHabits.length === 0) {
    hints.push(behaviors.length > 0 && behaviorIds.length === 0
      ? t('panel.missingBehaviorForHabitHint')
      : t('panel.noHabitsAvailableHint'));
  }
  // El hábito obligatorio SÍ elegible vivía como aviso dentro de la fila de hábitos, donde se leía
  // como una etiqueta más y el usuario no lo registraba. Baja aquí, con el resto de lo que falta
  // para poder enviar, que es donde ya mira cuando el botón no responde.
  if (hasFundamental && dailyHabitRequired && habitIds.length === 0 && availableHabits.length > 0) {
    hints.push(t('panel.habitRequiredHint'));
  }
  return hints;
}

const SUCCESS_MS = 2400;
// Centinela que manda el backend en `maxFundamentalsPerRecognition` cuando el toggle "Limitar
// cantidad de Fundamentales" está apagado (ConfigRecognitionPolicyProvider.cs usa `int.MaxValue`
// para "sin tope"). Mostrarlo tal cual en la etiqueta imprimía "Fundamental (máx. 2147483647)".
const UNBOUNDED_FUNDAMENTALS = 2147483647;

interface ReconocerPanelProps {
  /** `onSent` permite al contenedor reaccionar (p.ej. scroll al muro). */
  onSent?: () => void;
  /** REQ-120 — receptores precargados (p.ej. "Reconocer a X" desde el perfil de un compañero). */
  initialRecipients?: PersonSummary[];
  /** REQ-106 — abre el modal de carga masiva (botón embebido, H-42). Oculto para OUs sin permiso. */
  onOpenBulk?: () => void;
  canBulk?: boolean;
  /** Id de la campaña Brillamos activa, si la hay — viaja como `campanaId` (metadato de campaña,
   *  ver `campanaSecundariaId`/promoción). `null`/ausente cuando no hay campaña vigente. */
  campaignId?: string | null;
  /**
   * `true` cuando el envío pertenece al programa Brillamos — decide el `origen` ('Brillamos' vs
   * 'Diario') independientemente de si hay campaña activa. Sin esta prop el tag "Recibido en" del
   * post-it caía al fallback de Fundamentales para cualquier envío desde Brillamos sin campaña
   * vigente (`PostItTagsArea`, `postitShared.tsx`).
   *
   * Lo enciende `/app/brillamos` (`BrillamosView`, siempre) y el modal «Reconocer a X» del perfil de
   * un compañero (`PerfilView`, solo si hay campaña Brillamos vigente para quien envía): ese modal se
   * ofrece haya campaña o no, y el envío tiene que quedar atribuido al programa que rige.
   */
  isBrillamosScreen?: boolean;
}

/** Panel de envío de reconocimiento. */
export function ReconocerPanel({
  onSent, initialRecipients, onOpenBulk, canBulk, campaignId, isBrillamosScreen = false,
}: Readonly<ReconocerPanelProps>) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language;
  const { demoUser } = useUserContext();

  const catalog = useRecognitionCatalog();
  const create = useCreateRecognition();
  // En la pantalla de Brillamos las reglas salen del programa Brillamos (aunque no haya campaña que
  // pasar por campaignId); en el resto se deriva de la campaña o cae a Fundamentales.
  const rules = useRecognitionRules(campaignId, isBrillamosScreen ? 'Brillamos' : undefined);
  const fundRowRef = useRef<HTMLDivElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  // Config de OU reflejada en el front (el backend es la fuente de verdad y valida con 422).
  // D-5: el tope de Fundamentales y la delegación se derivan del flag GDPR de la OU y del
  // catálogo de seis roles — ver `access/useRecognitionPermissions.ts`.
  const { canDelegate, isGdprRestricted } = useRecognitionPermissions();
  // `multiFundamentalEnabled` ya incorpora el flag GDPR (Europa fuerza `false`), así que el tope
  // se resuelve con un único ternario en vez de anidar dos (evita sonarjs/no-nested-conditional).
  const multiFundamentalEnabled = !isGdprRestricted && (rules.data?.multiFundamental ?? true);
  // El tope llega RESUELTO del backend (misma lógica que ConfigRecognitionPolicyProvider); aquí solo
  // aplicamos el piso GDPR/`!multiFundamental` (⇒ 1). Antes estaba hardcodeado a 2, así que una
  // configuración de 4 nunca se respetaba; ahora se lee `maxFundamentalsPerRecognition` (default 2 si
  // el back no lo manda). El valor centinela `int.MaxValue` = sin tope: el picker se acota solo por el
  // catálogo, porque `toggleFundamental` compara contra `funds` disponibles.
  const maxFundamentals = !multiFundamentalEnabled ? 1 : (rules.data?.maxFundamentalsPerRecognition ?? 2);
  const fundamentalsUnbounded = maxFundamentals >= UNBOUNDED_FUNDAMENTALS;
  // Multinominación: Europa (compliance) siempre 1, fuera de Europa manda el toggle global de Gestión.
  const multinominationEnabled = !isGdprRestricted && (rules.data?.multinomination ?? true);
  const dailyHabitRequired = rules.data?.dailyHabitsMandatory ?? false;
  const commentRequired = rules.data?.commentRequired ?? true;
  const minCommentChars = rules.data?.minCommentChars ?? 0;
  // WP-F3 / CPE-158 — la OU puede no ofrecer «Enviar reconocimiento». Se gatea AQUÍ y no en
  // los tres sitios que montan el panel: repartirlo entre ellos garantiza que uno se olvide.
  // 🔴 El hook YA compone con el kill-switch, así que `enviarHabilitado` incluye a
  // `sendingDisabled` — no se suman las dos condiciones, que sería tenerlo en dos sitios.
  const enviarHabilitado = useVistaHabilitada('enviarReconocimiento', isBrillamosScreen ? 'Brillamos' : undefined);
  const sendingDisabled = !enviarHabilitado;
  // R7 — anticipan lo que hoy solo se enforcea al enviar (excepción de localidad activa): una
  // ventana de horario restringido bloquea el envío igual que el kill-switch; el tope efectivo es
  // solo informativo (el conteo real del emisor no viaja en este DTO).
  const scheduleRestricted = rules.data?.scheduleRestricted ?? false;
  const weeklySendLimitEffective = rules.data?.weeklySendLimitEffective ?? null;
  const anonymousAllowed = rules.data?.anonymousAllowed ?? true;

  // T5 — check "enviar también a Brillamos": solo en la pantalla Reconocer (no en /app/brillamos),
  // solo si Gestión lo permite Y existe una campaña Brillamos programada a futuro para la OU (el
  // backend resuelve/valida; aquí solo decidimos si OFRECER el check para no mostrarlo "en vano").
  const allowPromoteToBrillamos = rules.data?.allowPromoteToBrillamos ?? false;
  const brillamosGateOu = !isBrillamosScreen && allowPromoteToBrillamos ? (demoUser.ou || undefined) : undefined;
  const nextBrillamos = useNextBrillamos(brillamosGateOu);
  const showSendToBrillamos = Boolean(brillamosGateOu) && Boolean(nextBrillamos.data);

  const [recipients, setRecipients] = useState<PersonSummary[]>(initialRecipients ?? []);
  const [onBehalfOf, setOnBehalfOf] = useState<PersonSummary | null>(null);
  // Pestañas "A mi nombre" / "En representación de otro": separan visualmente el modo de envío
  // de la lista de destinatarios, que antes quedaban pegados (confusión reportada por usuarios).
  const [delegateMode, setDelegateMode] = useState<'self' | 'onBehalf'>('self');
  const [fundamentalIds, setFundamentalIds] = useState<string[]>([]);
  const [behaviorIds, setBehaviorIds] = useState<string[]>([]);
  const [habitIds, setHabitIds] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [sendToBrillamos, setSendToBrillamos] = useState(false);
  const [capHint, setCapHint] = useState(false);
  const [sent, setSent] = useState(false);

  // La caja del mensaje arranca en 4 líneas y crece con lo escrito hasta 10; de ahí en adelante hace
  // scroll. Los topes son estilo y viven en `.msg-autogrow` (recognition.css); el hook solo escribe
  // la altura medida del contenido.
  useAutoGrowTextarea(messageRef, message);

  // Guarda de cambios sin guardar: el borrador está "sucio" cuando el usuario ya empezó a redactar
  // (mensaje, o algún Fundamental/comportamiento/hábito elegido) y aún no ha enviado. Los receptores
  // prellenados desde el perfil (?receptor=) NO cuentan por sí solos: no son trabajo que perder. Con
  // esto, un clic en la campana que navegue a otra pantalla pide confirmación antes (y beforeunload
  // cubre recargar/cerrar). Ver useNavigationGuard en la campana (components.tsx).
  const hasUnsavedDraft = !sent && (
    message.trim().length > 0
    || fundamentalIds.length > 0
    || behaviorIds.length > 0
    || habitIds.length > 0
  );
  useUnsavedChangesGuard(hasUnsavedDraft);

  // WP-F5 — dos filtros DISTINTOS, y hay que aplicar los dos:
  //  · `isActive` es el borrado lógico del CATÁLOGO, que administra Pulso (D-27);
  //  · `fundamentalesDeshabilitados` es lo que ESTE programa ha retirado (bloque «Fundamentales
  //    disponibles» de Gestión), y llega por `rules` porque el documento de Gestión es
  //    AdminGlobal-only y aquí hay un colaborador.
  // Ofrecer uno retirado no es cosmético: el servidor rechaza el envío con 422.
  const funds: FundamentalDto[] = useMemo(
    () => fundamentalesOfrecibles(catalog.data, rules.data?.fundamentalesDeshabilitados),
    [catalog.data, rules.data?.fundamentalesDeshabilitados],
  );
  // Memoizado para que las referencias sean estables entre renders (evita bucles en los efectos de poda).
  const selectedFunds = useMemo(
    () => funds.filter((f) => fundamentalIds.includes(f.id)),
    [funds, fundamentalIds],
  );
  const behaviors: AttitudeBehaviorDto[] = useMemo(
    () => selectedFunds.flatMap((f) => (f.attitudes ?? []).filter((a) => a.isActive)),
    [selectedFunds],
  );
  const availableHabits = useMemo(() => {
    const list = selectedFunds.flatMap((f) => availableHabitsFor(f, behaviorIds));
    return Array.from(new Map(list.map((h) => [h.id, h])).values());
  }, [selectedFunds, behaviorIds]);
  // Demo: `HABIT_COLORS` colorea cada hábito según el Fundamental al que pertenece
  // (ReconocerPanel.tsx del demo, líneas 20-31 — en la práctica es el color del Fundamental).
  // Aquí se deriva del catálogo real en vez de una tabla estática de nombres en inglés.
  const habitColorById = useMemo(() => {
    const map = new Map<string, string>();
    selectedFunds.forEach((f, idx) => {
      const color = getFundamentalColor(f, funds.indexOf(f));
      availableHabitsFor(f, behaviorIds).forEach((h) => map.set(h.id, color));
      void idx;
    });
    return map;
  }, [selectedFunds, behaviorIds, funds]);

  // Poda hábitos/comportamientos que dejaron de estar disponibles al cambiar la selección.
  // Devuelve la MISMA referencia cuando no hay nada que podar → React descarta el update (sin bucle).
  useEffect(() => {
    const availIds = new Set(availableHabits.map((h) => h.id));
    setHabitIds((prev) => {
      const next = prev.filter((id) => availIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [availableHabits]);
  useEffect(() => {
    const behaviorSet = new Set(behaviors.map((b) => b.id));
    setBehaviorIds((prev) => {
      const next = prev.filter((id) => behaviorSet.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [behaviors]);

  const toggleFundamental = (id: string) => {
    setCapHint(false);
    setFundamentalIds((prev) => {
      if (prev.includes(id)) return prev.filter((f) => f !== id);
      if (maxFundamentals === 1) return [id];
      if (prev.length >= maxFundamentals) { setCapHint(true); return prev; }
      return [...prev, id];
    });
  };
  const toggleIn = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const resetForm = () => {
    setRecipients([]);
    setOnBehalfOf(null);
    setDelegateMode('self');
    setFundamentalIds([]);
    setBehaviorIds([]);
    setHabitIds([]);
    setMessage('');
    setAnonymous(false);
    setSendToBrillamos(false);
    setCapHint(false);
  };

  const messageOk = commentRequired
    ? message.trim().length >= Math.max(minCommentChars, 1)
    : message.trim().length === 0 || message.trim().length >= minCommentChars;

  const canSubmit =
    recipients.length > 0 && fundamentalIds.length > 0 && messageOk
    && (!dailyHabitRequired || habitIds.length > 0)
    && !sendingDisabled
    && !scheduleRestricted
    && !create.isPending;

  const missingHints = buildMissingHints({
    recipients,
    fundamentalIds,
    messageOk,
    minChars: commentRequired ? Math.max(minCommentChars, 1) : minCommentChars,
    dailyHabitRequired,
    habitIds,
    availableHabits,
    behaviors,
    behaviorIds,
    sendingDisabled,
    scheduleRestricted,
    isPending: create.isPending,
  }, t);

  const handleSubmit = () => {
    if (!canSubmit) return;
    const body: CreateRecognitionBody = {
      receptorIds: recipients.map((r) => r.id),
      fundamentalIds,
      mensaje: message.trim(),
      behaviorIds,
      habitIds,
      origen: resolveOrigin(Boolean(onBehalfOf), isBrillamosScreen),
      // T4 — "a nombre de otro" nunca es anónimo (el emisor efectivo es el delegado, debe verse).
      // Defensa extra: `anonymous` puede haber quedado en `true` de un estado marcado antes de
      // que `rules` resolviera `anonymousAllowed` — el backend sigue siendo la autoridad real.
      anonimo: onBehalfOf ? false : (anonymous && anonymousAllowed),
      campanaId: campaignId ?? null,
      etiquetaGrupal: recipients.length > 1 ? t('panel.groupTag') : null,
      onBehalfOfUserId: onBehalfOf?.id ?? null,
      // T5 — solo se envía si el check está visible (gate de Gestión + Brillamos próxima) y marcado.
      sendToBrillamos: showSendToBrillamos && sendToBrillamos,
    };
    create.mutate(body, {
      onSuccess: () => {
        setSent(true);
        onSent?.();
        setTimeout(() => { setSent(false); resetForm(); }, SUCCESS_MS);
      },
    });
  };

  if (sent) {
    return (
      <div className="rp-single rp-sent">
        <div className="rp-sent-icon">✨</div>
        <h3>{t('panel.sentTitle')}</h3>
        <p>{t('panel.sentSubtitle')}</p>
      </div>
    );
  }

  const errorStatus = getRecognitionErrorStatus(create.error);
  const errorMsg = create.isError
    ? recognitionSendErrorMessage(create.error, t, t('panel.sendErrorGeneric'))
    : null;
  // El aviso de "acepta los términos legales" SOLO cuando el backend no dio un mensaje propio
  // (403 con cuerpo vacío). Un 403 CON mensaje ya dice el motivo real —sea o no legal— y añadir
  // este texto encima lo contradice o lo duplica (el mensaje del gate legal YA es esta misma frase).
  const showLegalHint = errorStatus === 403 && errorMsg === t('panel.sendErrorGeneric');

  return (
    <div className="rp-single">
      {/* Cabecera con mascota — demo H-36 */}
      <div className="rp-header-row">
        <img src="/persona-torso.svg" alt="" className="rp-header-mascot" />
        <div>
          <h2 className="rp-single-title">{t('panel.title')}</h2>
          {/* QA (2026-08-28) — "Reconoce a personas o a un equipo completo" se quitó a propósito:
              no existe (ni existió) un selector real de EQUIPO como unidad — el flujo siempre fue
              búsqueda de personas una por una, con una etiqueta cosmética ("Equipo") cuando hay más
              de un receptor. El texto describía una capacidad que no está implementada. */}
        </div>
      </div>

      <StateBlock
        isLoading={catalog.isLoading}
        isError={catalog.isError}
        error={catalog.error}
        isEmpty={funds.length === 0}
        emptyText={t('catalog.empty')}
        onRetry={() => { catalog.refetch(); }}
      >
        {/* A nombre de (delegación) — solo roles con permiso; el backend revalida CanDelegate. H-43. */}
        {canDelegate && (
          <DelegateSection
            mode={delegateMode}
            onModeChange={(mode) => { setDelegateMode(mode); if (mode === 'self') setOnBehalfOf(null); }}
            onBehalfOf={onBehalfOf}
            onPick={setOnBehalfOf}
            onRemove={() => setOnBehalfOf(null)}
            excludeId={demoUser.userId}
          />
        )}

        {/* Personas */}
        <div className="rp-section">
          <label className="rp-section-label">{t('panel.recipientsLabel')}</label>
          {recipients.length > 0 && (
            <div className="rp-chips-row">
              {recipients.map((p) => (
                <span key={p.id} className="rp-chip-person">
                  <span className="rp-chip-av">{initials(p)}</span>
                  {p.displayName ?? p.email}
                  <button className="btn btn--secondary"
                    type="button"
                    onClick={() => setRecipients((prev) => prev.filter((r) => r.id !== p.id))}
                    aria-label={t('common.remove')}
                  >
                    <i className="fas fa-xmark" />
                  </button>
                </span>
              ))}
            </div>
          )}
          {/* `value` queda SIEMPRE en `null`: los receptores se pintan como chips arriba (multi-
              selección), así que el picker nunca debe quedarse con la elección puesta — cada
              selección se traslada a la lista y el campo sigue libre para el siguiente destinatario. */}
          <PersonPicker
            value={null}
            inputId="reconocer-recipients-input"
            placeholder={t('panel.recipientsPlaceholder')}
            excludeIds={[demoUser.userId, ...recipients.map((r) => r.id), ...(onBehalfOf ? [onBehalfOf.id] : [])]}
            onChange={(persona) => {
              if (!persona) return;
              const elegida = aPersonSummary(persona);
              setRecipients((prev) => {
                if (prev.some((recipiente) => recipiente.id === elegida.id)) return prev;
                if (!multinominationEnabled) return [elegida];
                return [...prev, elegida];
              });
            }}
          />
        </div>

        {/* Fundamentales — tarjeta vertical con icono, demo H-37 */}
        <div className="rp-section" ref={fundRowRef}>
          <label className="rp-section-label">
            {fundamentalsUnbounded ? t('panel.fundamentalLabelUnbounded') : t('panel.fundamentalLabel', { max: maxFundamentals })}
          </label>
          <div className="rp-fund-row">
            {funds.map((f, idx) => {
              const active = fundamentalIds.includes(f.id);
              return (
                <button
                  key={f.id}
                  type="button"
                  className={`rp-fund-pill ${active ? 'rp-fund-pill--active' : ''}`}
                  style={{ background: getFundamentalColor(f, idx) }}
                  onClick={() => toggleFundamental(f.id)}
                >
                  <img src={getFundamentalIcon(f, idx)} alt="" className="rp-fund-pill-icon" />
                  <span>{getFundamentalName(f, lang)}</span>
                  {active && <i className="fas fa-check" />}
                </button>
              );
            })}
          </div>
          {capHint && <p className="rp-note">{t('panel.maxFundamentalsHint', { max: maxFundamentals })}</p>}
        </div>

        {/* Resumen acumulativo con icono + botón Editar — demo H-40 */}
        {selectedFunds.length > 0 && (
          <div className="rp-section rp-resumen-section">
            <label className="rp-section-label">{t('panel.summaryLabel')}</label>
            <div className="rp-resumen-list">
              {selectedFunds.map((f) => {
                const idx = funds.indexOf(f);
                const color = getFundamentalColor(f, idx);
                const behaviorNames = (f.attitudes ?? [])
                  .filter((b) => behaviorIds.includes(b.id))
                  .map((b) => getBehaviorName(b, lang))
                  .join(', ');
                const habitText = availableHabitsFor(f, behaviorIds)
                  .filter((h) => habitIds.includes(h.id))
                  .map((h) => getHabitName(h, lang))
                  .slice(0, 1)[0];
                return (
                  <div key={f.id} className="rp-resumen-row" style={{ borderColor: `${color}30` }}>
                    <span className="rp-resumen-fund-tag" style={{ background: color }}>
                      <img src={getFundamentalIcon(f, idx)} alt="" className="rp-resumen-fund-tag-icon" />
                      <span>{getFundamentalName(f, lang)}</span>
                      <button type="button" className="rp-resumen-remove" onClick={() => toggleFundamental(f.id)} aria-label={t('common.remove')}>
                        <i className="fas fa-xmark" />
                      </button>
                    </span>
                    {behaviorNames && <span className="rp-resumen-detail">{behaviorNames}</span>}
                    {habitText && <span className="rp-resumen-detail rp-resumen-habit">{habitText}</span>}
                    <button
                      type="button"
                      className="rp-resumen-edit"
                      onClick={() => fundRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                    >
                      {t('panel.edit')}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Comportamientos (solo fundamentales con actitudes) — con icono, demo H-38 */}
        {behaviors.length > 0 && (
          <div className="rp-section">
            <label className="rp-section-label">{t('panel.behaviorsLabel')}</label>
            <div className="rp-behaviors-row">
              {behaviors.map((b, idx) => {
                const active = behaviorIds.includes(b.id);
                return (
                  <button
                    key={b.id}
                    type="button"
                    className={`rp-behavior-chip ${active ? 'rp-behavior-chip--active' : ''}`}
                    onClick={() => setBehaviorIds((prev) => toggleIn(prev, b.id))}
                  >
                    <img src={getBehaviorIcon(b, idx)} alt="" className="rp-behavior-icon" />
                    <span>{getBehaviorName(b, lang)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Daily habits — coloreados por Fundamental, demo H-39 */}
        {availableHabits.length > 0 && (
          <div className="rp-section">
            <label className="rp-section-label">⚡ {t('panel.habitsLabel')}</label>
            <div className="rp-habits-row">
              {availableHabits.map((h) => {
                const active = habitIds.includes(h.id);
                const habitColor = habitColorById.get(h.id) ?? 'var(--cp-orange)';
                return (
                  <button
                    key={h.id}
                    type="button"
                    className={`rp-habit-tag ${active ? 'rp-habit-tag--active' : ''}`}
                    onClick={() => setHabitIds((prev) => toggleIn(prev, h.id))}
                    style={active
                      ? { borderColor: habitColor, background: 'var(--white)', color: habitColor }
                      : { background: `color-mix(in srgb, ${habitColor} 8%, transparent)`, borderColor: `color-mix(in srgb, ${habitColor} 20%, transparent)` }}
                  >
                    {active && <i className="fas fa-check" />}
                    {getHabitName(h, lang)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Mensaje — demo H-41 */}
        <div className="rp-section">
          <label className="rp-section-label">💬 {t('panel.messageLabel')}</label>
          <div className="msg-emoji-row">
            <EmojiPickerButton
              onSelect={(emoji) => {
                const { next, cursor } = insertAtCursor(messageRef.current, message, emoji);
                setMessage(next);
                requestAnimationFrame(() => {
                  messageRef.current?.focus();
                  messageRef.current?.setSelectionRange(cursor, cursor);
                });
              }}
            />
          </div>
          <textarea
            ref={messageRef}
            className="rp-msg-area msg-autogrow"
            rows={4}
            maxLength={MENSAJE_MAX_LENGTH}
            value={message}
            placeholder={placeholderDelMensaje(rules.data?.messagePlaceholder, i18n.language, t('panel.messagePlaceholder'))}
            onChange={(e) => setMessage(e.target.value)}
          />
          {/* Checkbox anónimo — solo integrado, sin equivalente en el demo (decisión D-F4-1,
              ver docs/paridad/MAPA-CLASES.md / ESTADO.md): se restila al lenguaje visual del
              demo en vez de retirarse.
              T4 (decisión Adriana 2026-08-07): al reconocer "a nombre de otro" (delegación) la opción
              anónima DESAPARECE — un reconocimiento delegado no puede ser anónimo. */}
          {!onBehalfOf && anonymousAllowed && (
            <label className="rp-anon">
              <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
              {t('panel.anonymous')}
            </label>
          )}
          {/* T5 — check "enviar también a Brillamos": junto al de anónimo, solo si Gestión lo permite
              y hay una campaña Brillamos próxima programada (si no, no se ofrece). */}
          {showSendToBrillamos && (
            <label className="rp-anon rp-anon--brillamos">
              <input type="checkbox" checked={sendToBrillamos} onChange={(e) => setSendToBrillamos(e.target.checked)} />
              {t('panel.sendToBrillamos')}
            </label>
          )}
          {sendingDisabled && (
            <p className="rp-error" role="alert">
              <i className="fas fa-circle-info" /> {t('panel.sendingDisabledHint')}
            </p>
          )}
          {/* R7 — excepción de localidad `HorarioRestringido` activa AHORA: mismo tratamiento que el
              kill-switch (bloquea + aviso), pero no se duplica el mensaje si ya está sendingDisabled. */}
          {!sendingDisabled && scheduleRestricted && (
            <p className="rp-error" role="alert">
              <i className="fas fa-circle-info" /> {t('panel.scheduleRestrictedHint')}
            </p>
          )}
          {/* R7 — tope efectivo de la localidad: solo informativo (no deshabilita), el conteo real
              del emisor no viaja en este DTO. */}
          {weeklySendLimitEffective != null && (
            <p className="rp-note">{t('panel.weeklyLimitHint', { count: weeklySendLimitEffective })}</p>
          )}
          {/* Recordatorio junto al botón de envío: sin esto, al llegar hasta acá el usuario ya
              perdió de vista la pestaña "En representación de otro" elegida arriba. */}
          {onBehalfOf && (
            <p className="rp-note rp-note--onbehalf">
              <i className="fas fa-user-pen" />
              {' '}{t('panel.onBehalfActiveBanner', { name: onBehalfOf.displayName ?? onBehalfOf.email })}
            </p>
          )}
          {/* Aviso preventivo del tope: nada hasta los últimos 100 caracteres, después el conteo de
              lo que queda. El textarea ya está topado en MENSAJE_MAX_LENGTH, así que el usuario no
              puede pasarse — lo que faltaba era que viera venir el límite. */}
          <MessageLimitHint length={message.length} />
          <div className="rp-send-row">
            <span className="rp-char-count">{t('panel.charCount', { count: message.length })}</span>
            <button type="button" className="btn btn--send" onClick={handleSubmit} disabled={!canSubmit}>
              <i className="fas fa-paper-plane" />
              {create.isPending ? t('panel.sending') : t('panel.send')}
            </button>
          </div>
          {missingHints.length > 0 && (
            <ul className="rp-missing-list" role="status">
              {missingHints.map((hint) => (
                <li key={hint}>{hint}</li>
              ))}
            </ul>
          )}
        </div>

        {errorMsg && (
          <div className="rp-error" role="alert">
            <i className="fas fa-triangle-exclamation" />
            <span>
              {errorMsg}
              {showLegalHint && <><br />{t('panel.legalHint')}</>}
            </span>
          </div>
        )}

        {/* QA (2026-08-28) — "Tu reconocimiento es visible para el equipo y el líder" se quitó a
            propósito: no hay ninguna regla de visibilidad real (en RecognitionRulesDto/
            GestionSettingsDto ni en el flujo de envío) que respalde esa frase específica — era
            texto heredado del demo sin contraparte funcional. */}
      </StateBlock>

      {/* Carga masiva embebida — demo H-42. Oculta para OUs sin permiso (Europa no la ve). */}
      {canBulk && (
        <div className="rp-bulk-section">
          <div className="rp-bulk-divider"><span>{t('bulk.or')}</span></div>
          <div className="rp-bulk-card">
            <div className="rp-bulk-header">
              <i className="fas fa-file-csv" />
              <div>
                <h4 className="rp-bulk-title">{t('bulk.title')}</h4>
                <p className="rp-bulk-desc">{t('bulk.subtitle')}</p>
              </div>
            </div>
            <div className="rp-bulk-actions">
              <button type="button" className="rp-bulk-upload" onClick={() => onOpenBulk?.()}>
                <i className="fas fa-upload" /> {t('bulk.upload')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReconocerPanel;
